'use client';

import appLogger from '@/utils/logger';
import { useAuth } from '@/contexts/AuthProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { logout } from '@/services';
import { getProfileAvatar } from '@/services/bots';
import { useBackgroundAnalysis } from '@/hooks/useBackgroundAnalysis';
import { useNotifications } from '@/contexts/NotificationProvider';
import { countUnseenActiveNotifications } from '@/utils/notificationHelpers';
import { PATH } from '@/constants';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { FC, ReactNode, useEffect, useRef, useState, useMemo } from 'react';
import { Bell, Crown } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import MobileHeader from './MobileHeader';
import Sidebar, { UserOptions } from './Sidebar';
import {
  ProtectedLayoutProvider,
  useProtectedLayoutContext,
} from './context/ProtectedLayoutProvider';
import { GettingStartedChecklist } from '@/views/dashboard/components/GettingStartedChecklist';
import NotificationsDrawer from '@/views/tasks/components/NotificationsDrawer';
import { useAePreparedNotifications } from '@/hooks/useAePreparedNotifications';
// FreeTrialBanner removed — can-redeem-free-credits always returns false
import TrialStatusBanner from '@/components/TrialStatusBanner';
import WorkspaceLoadErrorBanner from '@/components/WorkspaceLoadErrorBanner';
import AnnouncementBanners from '@/components/announcements/AnnouncementBanners';
import {
  useCurrentSubscription,
  useWorkspaceSubscriptionsOptimized,
  useIsMarketingExpired,
} from '@/hooks/useSubscription';
import MarketingTrialExpiredOverlay from '@/components/shared/MarketingTrialExpiredOverlay';
import { useDebouncedNoSubscriptionGate } from '@/hooks/useDebouncedNoSubscriptionGate';
import { useMountLogger } from '@/utils/useMountLogger';

interface IProtectedLayoutProps {
  children: ReactNode;
}

/** Matches the `right-6` offset on the floating top-right nav cluster. */
const TOP_NAV_RIGHT_OFFSET = 24;
/** Breathing room between a page header's text and the nav cluster. */
const TOP_NAV_GUTTER = 16;

/**
 * Full-page screen shown when the workspace has no active subscription.
 * Redirects to the onboarding subscribe page which has the full trial signup UI
 * with pricing, features, and Stripe checkout integration.
 */
const NoSubscriptionScreen: FC<{ isMarketing: boolean }> = ({ isMarketing }) => {
  const router = useRouter();

  const title = isMarketing ? 'Book a Meeting with Sunder to Continue' : 'Subscription Required';
  const description = isMarketing
    ? 'Your Marketing trial has ended. To keep MarketBetter running for your team, book a meeting with Sunder to set up the right plan.'
    : 'Your trial has ended or your subscription is inactive. Subscribe to continue using MarketBetter.';
  const subDescription = isMarketing
    ? 'Your data and workflows are preserved. Sunder will review your goals and tailor a Marketing plan to your volume.'
    : 'Your data and workflows are preserved. Subscribe to pick up where you left off.';

  return (
    <div className='flex flex-col items-center justify-center min-h-[80vh] px-4 text-center'>
      <div className='rounded-full bg-blue-50 p-4 mb-5'>
        <Crown className='w-10 h-10 text-blue-500' />
      </div>
      <h2 className='text-2xl font-bold text-slate-900 mb-2'>{title}</h2>
      <p className='text-sm text-slate-500 max-w-md mb-2'>{description}</p>
      <p className='text-xs text-slate-400 max-w-sm mb-6'>{subDescription}</p>
      <div className='flex items-center gap-3'>
        {isMarketing ? (
          <>
            <Button onClick={() => router.push('/book-demo')}>Book a Meeting with Sunder</Button>
            <Button
              variant='outline'
              onClick={() => {
                window.location.href =
                  'mailto:team@marketbetter.ai?subject=Marketing Plan Discussion';
              }}
            >
              Email Team
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => router.push('/onboarding/subscribe')}>Subscribe</Button>
            <Button
              variant='outline'
              onClick={() => router.push('/settings?tab=billing-and-usage')}
            >
              View Billing Settings
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const LayoutContent: FC<IProtectedLayoutProps> = ({ children }) => {
  const layoutInstanceId = useMountLogger('ProtectedLayout.LayoutContent');
  const { expandedSidebar, isMobileMatches, toggleSidebarMode, isNotificationsDrawerOpen, setIsNotificationsDrawerOpen } =
    useProtectedLayoutContext();
  const { user } = useAuth();
  const workspaceContext = useWorkspace();
  const router = useRouter();
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { notifications, notificationsSeenAt, markNotificationsSeen } = useNotifications();
  const isAe = workspaceContext?.isAe === true;
  const activeWorkspaceId = workspaceContext?.activeWorkspace?.vendorid ?? null;
  // Every user's own notifications (an AE's prepared work, a rep's monthly loop heads-up); only AEs poll.
  const aePreparedNotifications = useAePreparedNotifications(true, true, activeWorkspaceId, isAe);

  const { data: subscriptionsData } = useWorkspaceSubscriptionsOptimized({
    enabled: !!workspaceContext?.activeWorkspace?.vendorid,
    workspaceId: workspaceContext?.activeWorkspace?.vendorid,
  });
  const hasActiveTrial = useMemo(() => {
    return subscriptionsData?.subscriptions?.some((sub) => sub.status === 'trial') ?? false;
  }, [subscriptionsData]);

  // Badge counts active notifications the user has NOT seen since last opening
  // the drawer. Opening marks them seen so the badge clears and does not stick
  // on stale active notifications (missed terminal events keep items "active"
  // for up to 72h). Uses shared logic from utils/notificationHelpers.ts.
  const activeNotificationCount = useMemo(() => {
    return countUnseenActiveNotifications(notifications, notificationsSeenAt)
      + (aePreparedNotifications.data?.unread_count ?? 0);
  }, [notifications, notificationsSeenAt, aePreparedNotifications.data?.unread_count]);

  // Mark notifications seen whenever the drawer opens (covers all open paths).
  useEffect(() => {
    if (isNotificationsDrawerOpen) {
      markNotificationsSeen();
    }
  }, [isNotificationsDrawerOpen, markNotificationsSeen]);

  // Monitor background AI analyses
  useBackgroundAnalysis();

  const pathname = usePathname() ?? '';
  const isWorkflowDetailsPage = pathname.match(/^\/workflows\/[^\/]+$/);
  const isSettingsPage = pathname.startsWith('/settings');
  const isDashboardPage = pathname === '/dashboard' || pathname === '/analytics/overview';
  const isOnboardingPage = pathname.startsWith('/onboarding');
  const isBotPage = pathname === '/bot';
  // AI Context is a full-header workspace (its own toolbar with search + actions),
  // so — like settings/dashboard/bot — it suppresses the floating top-right nav
  // cluster instead of fighting it for the same corner.
  const isAiContextPage = pathname === '/ai-context';
  // Same reasoning as /workflows/[id]: a single-template drill-down with its own
  // Cancel/Save actions in the same corner. Deliberately narrower than
  // /campaigns/outreach-templates itself (the list), which shares Campaigns' own
  // PageHeader/tab strip and reserves space for the cluster the normal way — hiding it
  // there too would make the nav pop in and out as a rep switches the Campaigns/Templates
  // tab, which is worse than the crowding this fixes.
  const isOutreachTemplateEditorPage = pathname.match(/^\/campaigns\/outreach-templates\/[^/]+$/);
  const hidesTopNav = !!(
    isWorkflowDetailsPage ||
    isSettingsPage ||
    isDashboardPage ||
    isOnboardingPage ||
    isBotPage ||
    isAiContextPage ||
    isOutreachTemplateEditorPage
  );

  useEffect(() => {
    if (isMobileMatches || isSettingsPage || isOnboardingPage) return;
    const handleNavigationShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key === '\\'
        && !(event.target instanceof HTMLElement && event.target.closest('input,textarea,[contenteditable=true]'))) {
        event.preventDefault();
        toggleSidebarMode();
      }
    };
    document.addEventListener('keydown', handleNavigationShortcut);
    return () => document.removeEventListener('keydown', handleNavigationShortcut);
  }, [isMobileMatches, isSettingsPage, isOnboardingPage, toggleSidebarMode]);

  const shellRef = useRef<HTMLDivElement>(null);
  const topNavRef = useRef<HTMLDivElement>(null);

  /**
   * The top-right nav cluster is absolutely positioned, so page headers can't see it
   * and their title/description used to run underneath it. Measure the compact
   * notifications + account cluster and publish the space it needs as
   * `--top-nav-inset`. `HUB_PAGE_CHROME.headerReserveRight` reserves exactly that on
   * md+ (below md the cluster is `hidden` and MobileHeader takes over).
   */
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    if (hidesTopNav) {
      // Cluster is `invisible`, not unmounted — it still measures, but reserving for it
      // would strand empty space on pages that deliberately suppress it.
      shell.style.setProperty('--top-nav-inset', '0px');
      return;
    }

    const cluster = topNavRef.current;
    if (!cluster) return;

    const publish = () => {
      const width = cluster.getBoundingClientRect().width;
      shell.style.setProperty(
        '--top-nav-inset',
        `${Math.ceil(width) + TOP_NAV_RIGHT_OFFSET + TOP_NAV_GUTTER}px`,
      );
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(cluster);
    return () => observer.disconnect();
  }, [hidesTopNav]);

  // GH#1101: Check subscription status early to prevent cascade of 403s.
  // /subscription/current is exempt from requireActiveSubscription, so this always succeeds.
  const { data: currentSubData, isLoading: isSubLoading } = useCurrentSubscription({
    enabled: !!workspaceContext?.activeWorkspace?.vendorid,
  });
  const hasNoSubscription =
    !isSubLoading &&
    currentSubData &&
    currentSubData.data === null &&
    !isSettingsPage &&
    !isOnboardingPage &&
    !!workspaceContext?.activeWorkspace;
  const confirmedNoSubscription = useDebouncedNoSubscriptionGate(
    !!hasNoSubscription,
    isSubLoading,
    !!currentSubData?.data,
  );

  // eslint-disable-next-line no-console
  console.log('[DIAG][RENDER] ProtectedLayout.LayoutContent', {
    instance: layoutInstanceId,
    pathname,
    isSubLoading,
    currentSubData_present: !!currentSubData,
    currentSubData_dataIsNull: currentSubData ? currentSubData.data === null : undefined,
    hasNoSubscription,
    confirmedNoSubscription,
    activeWorkspaceVendorId: workspaceContext?.activeWorkspace?.vendorid,
    activeWorkspaceObjPresent: !!workspaceContext?.activeWorkspace,
    timestamp: new Date().toISOString(),
  });

  const isMarketingOnly = workspaceContext?.isMarketingOnly ?? false;
  // After a marketing trial expires, setInactive() removes 'marketing' from
  // product_modules so isMarketingOnly flips false — fall back to the subscription-
  // history signal so the no-subscription screen still routes the user to book a
  // meeting with Sunder (instead of the sales Subscribe/Stripe copy).
  const isMarketingExpired = useIsMarketingExpired();
  const isMarketingForBilling = isMarketingOnly || isMarketingExpired;

  const init = async () => {
    const url = await getProfileAvatar();
    setAvatarUrl(url ?? '');
  };

  useEffect(() => {
    init();
  }, []); // Remove avatarUrl dependency to prevent infinite loop

  const handleLogout = async () => {
    await logout();
  };

  // CRITICAL FIX: Clear reb2b tracking data that causes unwanted redirects on page refresh
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Clear ALL reb2b-related localStorage keys that interfere with navigation
      // This prevents the third-party tracking script from causing redirects to /contents
      const reb2bKeys = ['_reb2bref', '_reb2bsessionID', '_reb2buid'];

      reb2bKeys.forEach((key) => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          appLogger.info(`Cleared reb2b key: ${key} to prevent redirect issues`);
        }
      });

      // Also clear any other keys that start with _reb2b
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('_reb2b')) {
          localStorage.removeItem(key);
          appLogger.info(`Cleared additional reb2b key: ${key}`);
        }
      });
    }
  }, []);

  return (
    <div className='page-wrapper mbn-layout flex h-screen max-h-screen overflow-hidden'>
      {!isOnboardingPage && <MobileHeader />}
      <div className={clsx('hidden md:block', isOnboardingPage && 'md:hidden')}>
        <Sidebar />
      </div>
      <div
        ref={shellRef}
        className={clsx(
          'group/shell mbn-main ml-0 flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden transition-all duration-200 ease-in-out',
          isOnboardingPage
            ? 'mt-0 h-screen max-h-screen'
            : 'mt-[var(--mobile-header-offset)] h-[calc(100dvh-var(--mobile-header-offset))] max-h-[calc(100dvh-var(--mobile-header-offset))] md:mt-0 md:h-screen md:max-h-screen',
          // Keep the content aligned with the navigation's token widths; settings stays expanded.
          !isOnboardingPage && (isSettingsPage || expandedSidebar)
            ? 'mbn-main-expanded'
            : !isOnboardingPage
              ? 'mbn-main-rail'
              : 'md:ml-0',
        )}
      >
        <div
          ref={topNavRef}
          className={clsx(
            'absolute top-[14px] right-6 z-50 hidden md:flex items-center gap-2.5',
            // A page-level detail drawer is pinned to the same corner and is
            // 560px wide, so this cluster always lands inside it. At z-50 it
            // painted ON the drawer: the bell and the avatar sat in the
            // drawer's own title bar, overlapping its close button.
            //
            // Hidden rather than out-ranked. Raising the drawer above z-50
            // would put it over `Dialog` too, which is also z-50 — and the
            // drawer opens one (delete opportunity), so the confirmation would
            // have rendered behind the thing that asked for it.
            'group-has-[[data-detail-drawer]]/shell:invisible',
            hidesTopNav && 'invisible',
          )}
        >
          {/* Recommended 1A design: header utilities are notifications + account only. */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  aria-label='Workflow notifications'
                  className='relative h-9 w-9 flex items-center justify-center rounded-[10px] border-0 bg-transparent shadow-none hover:bg-slate-100'
                  onClick={() => setIsNotificationsDrawerOpen(true)}
                >
                  <Bell className='h-[18px] w-[18px] text-slate-600' />
                  {activeNotificationCount > 0 && (
                    <span className='absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-medium text-white'>
                      {activeNotificationCount > 99 ? '99+' : activeNotificationCount}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Workflow Notifications</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <UserOptions
            avatarUrl={avatarUrl}
            user={user}
            handleLogout={handleLogout}
          />
        </div>
        <main className='bg-gray md:bg-inherit flex-1 min-h-0 flex flex-col max-w-full overflow-y-auto'>
          <WorkspaceLoadErrorBanner />
          {hasActiveTrial && !isSettingsPage && !isOnboardingPage && (
            <TrialStatusBanner compact className='mx-4 md:mx-6 mt-4 md:w-1/2' />
          )}
          {confirmedNoSubscription ? (
            <NoSubscriptionScreen isMarketing={isMarketingForBilling} />
          ) : (
            <>
              <AnnouncementBanners />
              {children}
            </>
          )}
        </main>
      </div>

      {/* Global Notifications Drawer */}
      <NotificationsDrawer
        isOpen={isNotificationsDrawerOpen}
        onClose={() => setIsNotificationsDrawerOpen(false)}
      />

      {/* GH#3837: ChatWidget removed -- it self-tracked every authenticated page load as an
          external visitor pageview. Public, consent-gated tracking already covers the intended
          use case via PublicTrackingScripts.tsx on the (public) route group. */}

      {/* GH#1280: Calendar overlay for expired marketing-only trials */}
      <MarketingTrialExpiredOverlay />
    </div>
  );
};

export const ProtectedLayout: FC<IProtectedLayoutProps> = ({ children }) => {
  useMountLogger('ProtectedLayout (outer)');
  return (
    <ProtectedLayoutProvider>
      <LayoutContent>{children}</LayoutContent>
    </ProtectedLayoutProvider>
  );
};
